import React, { useEffect, useRef, useState } from 'react'

import { calculatePercentage } from '../../calculatePercentage'

interface AudioSeekBarProps {
	currentTime: number
	duration: number
	disabled?: boolean
	ariaLabel?: string
	className?: string
	onSeek: (percentage: number) => void // 0..100, already rounded
}

/**
 * Scrubbable audio progress control shared by the v2 mini bar and fullscreen player.
 *
 * Backed by a native <input type="range"> so tap-to-seek and drag work correctly on
 * touch (react-rangeslider does not — its handleStart never fires onChange on a track
 * tap, so a tap commits a stale position). Native range is also a `slider` with
 * keyboard + ARIA for free.
 *
 * The audio timeline flows left→right, so the control always renders LTR.
 */
export default function AudioSeekBar({
	currentTime,
	duration,
	disabled = false,
	ariaLabel,
	className,
	onSeek,
}: AudioSeekBarProps): JSX.Element {
	// Mapping a percentage to a seek position needs a real duration — it's
	// 0/undefined before metadata loads and Infinity for streams, so treat all of
	// those as not seekable regardless of what the caller passed for `disabled`.
	const seekable = !disabled && Number.isFinite(duration) && duration > 0

	// Whether the user is mid-drag. Never affects rendered output (only `value`
	// does), so a plain ref is enough — and the tracking effect can read the
	// latest flag without depending on it.
	const dragging = useRef(false)

	// Displayed 0..100 position. Held in state (rather than derived every render) so
	// that committing a seek doesn't momentarily snap the bar back to the pre-seek
	// position while the new currentTime propagates into state.
	const [value, setValue] = useState(() => calculatePercentage(currentTime, duration))

	// Follow playback position when the user isn't interacting. Reacting only to
	// actual currentTime/duration changes (not to releasing a drag) is what prevents
	// the fill from jumping forward → back → forward on a tap: on release we leave
	// `value` at the sought position and let it resume tracking once the seeked
	// currentTime lands.
	useEffect(() => {
		if (dragging.current) return
		setValue(calculatePercentage(currentTime, duration))
	}, [currentTime, duration])

	// React fires onChange on EVERY value change for range inputs (it maps to the
	// native `input` event) — there is no commit event, so onChange only updates the
	// displayed value. The seek is committed on release (pointer/key up, or blur).
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		if (!seekable) return
		dragging.current = true
		setValue(Number(e.target.value))
	}

	const commit = (): void => {
		if (!seekable || !dragging.current) return
		dragging.current = false
		// Leave `value` where the user put it; the tracking effect resumes on the next
		// real currentTime update (which reflects the seek), so there's no snap-back.
		onSeek(Math.round(value))
	}

	// pointercancel (incoming call, browser scroll/gesture takeover) ends the
	// interaction without a pointerup — without this the drag flag would stay set
	// and the bar would freeze at the dragged position forever. Abort: discard the
	// dragged value and fall back to tracking playback.
	const cancelDrag = (): void => {
		if (!dragging.current) return
		dragging.current = false
		setValue(calculatePercentage(currentTime, duration))
	}

	return (
		<input
			type="range"
			min={0}
			max={100}
			step={1}
			dir="ltr"
			className={className}
			disabled={!seekable}
			value={value}
			aria-label={ariaLabel}
			style={{ ['--seek-fill' as string]: `${value}%` } as React.CSSProperties}
			onChange={handleChange}
			onPointerUp={commit}
			onKeyUp={commit}
			onBlur={commit}
			onPointerCancel={cancelDrag}
		/>
	)
}
